import { sentryLogger } from 'sentry'

import { apiEndpoints, apiURLOrigin } from './common'

import { get, patch, post } from '../request'

/**
 * Roles & Permissions (HQ).
 *
 * There is deliberately no deleteRole: a role can never be removed from Alfred,
 * and the backend refuses it too (User.role is PROTECT and the Postgres FK is
 * NO ACTION, so even raw SQL cannot drop a role somebody holds).
 */

// The request interceptor injects limit=25 on any oh-api GET without one, which
// would silently truncate the roles list. Ask for all of them.
export const fetchRoles = async () => {
  try {
    const response = await get(`${apiEndpoints.role}?limit=200`)
    return response.data?.results || response.data || []
  } catch (error) {
    sentryLogger(error, 'error in fetching roles')
    throw error
  }
}

export const createRole = async (data) => {
  try {
    const response = await post(apiEndpoints.role, data)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in creating role')
    throw error
  }
}

export const updateRole = async (uuid, data) => {
  try {
    // Trailing slash required — Django APPEND_SLASH would 404 the PATCH.
    const response = await patch(`${apiEndpoints.role}/${uuid}/`, data)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating role')
    throw error
  }
}

/**
 * Everyone who can hold a role: the admins and the teachers.
 *
 * One lean call. This used to merge /oh_users/admin_user?limit=500 with
 * /oh_users/teacher?limit=500 in the browser, which was both slow (those are the
 * full CRUD serializers -- 642KB for 500 teachers) and WRONG: there are 1,127
 * teachers, so limit=500 quietly dropped 627 of them off the list.
 */
export const fetchRoleHolders = async () => {
  try {
    const response = await get(apiEndpoints.roleHolders)
    return response.data?.results || []
  } catch (error) {
    sentryLogger(error, 'error in fetching role holders')
    throw error
  }
}

/**
 * Assign (or clear) a person's role.
 *
 * Goes through /base/edit_user, which is now a genuinely PARTIAL endpoint: a
 * field that is absent from the body is left alone. So {uuid, role} changes the
 * role and touches nothing else -- no need to re-post the person's name, phone
 * or teacher pay rates (re-posting them is what used to null the pay rates).
 */
export const assignRole = async (userUuid, roleUuid) => {
  try {
    const response = await post(`${apiURLOrigin}/base/edit_user`, {
      uuid: userUuid,
      role: roleUuid || null
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in assigning role')
    throw error
  }
}

/**
 * Map an admin to a centre.
 *
 * Alfred deliberately assigns ONE centre, so this REPLACES whatever the person
 * held before. That is not cosmetic: AdminUser.centres carries an m2m_changed
 * signal (oh-api apps/oh_users/admin_user/signals.py) that adds and removes the
 * person as a member of that centre's parent chat channels. The People screen
 * spells out what is being removed before it calls this.
 *
 * Only admins have centres — a teacher has no such field. Calling this for a
 * user with no AdminUser row 404s, which is why the column is read-only there.
 *
 * UpdateAdminUserSerializer is not partial and declares user as required, so the
 * body repeats the uuid that is already in the path.
 */
export const setAdminCentre = async (userUuid, centreUuid) => {
  try {
    // Trailing slash required — Django APPEND_SLASH would 404 the PATCH.
    const response = await patch(`${apiEndpoints.adminUser}/${userUuid}/`, {
      user: userUuid,
      centres: centreUuid ? [centreUuid] : []
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in setting admin centre')
    throw error
  }
}

/** Turn a person's Alfred access on or off. */
export const setAlfredAccess = async (userUuid, enabled) => {
  try {
    const response = await post(`${apiURLOrigin}/base/edit_user`, {
      uuid: userUuid,
      is_active: Boolean(enabled)
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating alfred access')
    throw error
  }
}

/**
 * The current user's Alfred access: { role, unrestricted, permissions[], enabled }.
 * Fetched once at boot; everything that gates reads it from redux.
 */
export const fetchMyAccess = async () => {
  try {
    const response = await get(apiEndpoints.me)
    return response.data?.alfred_access || null
  } catch (error) {
    sentryLogger(error, 'error in fetching alfred access')
    throw error
  }
}
